import React from 'react';
import PropTypes from 'prop-types';
import { omit, flowRight as compose } from 'lodash';
import { withRouter, useRouter } from 'next/router';
import { getRoutePath } from '@routes/routes.util';
import { withActiveLocale, withActiveLocalePropType } from '../withActiveLocale';

export const withRoutingPropType = PropTypes.shape({
  // next router props
  asPath: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
  route: PropTypes.string.isRequired,
  query: PropTypes.shape({}).isRequired,
  beforePopState: PropTypes.func,
  back: PropTypes.func,
  reload: PropTypes.func,

  // aliased/wrapper props
  pushRoute: PropTypes.func.isRequired,
  pushRouteAndScroll: PropTypes.func.isRequired,
  replaceRoute: PropTypes.func.isRequired,
  replaceRouteAndScroll: PropTypes.func.isRequired
});

const validMethods = ['pushRoute', 'replaceRoute', 'prefetchRoute'];

export function withRouting(BaseComponent) {
  function ComponentWithRouter(props) {
    /**
     * When this component is rendered on the server side, useRouter() returns null.
     * To prevent an error when destructuring the router object, we set it to an empty object.
     */
    const router = useRouter() ?? {};

    const resolveMethod = (method) => (validMethods.includes(method) ? method : 'pushRoute');

    /**
     * Wrappers to enforce consistency and ease of use for programatic route control.
     *
     * Method takes a Pathname or Route.name (OrganizationSettings) and will
     * append the activeLocale as needed. Locale can be overridden by explicitly passing one in the Pathname
     * or as a parameter. The second argument is params–an object that matches query parameters as defined by
     * the route definition (routes/routes.js).
     *
     * @param {string} - route Pathname (/en/organization) or Route.name (OrganizationSettings)
     * @param {object} - params - parameters for the named routes (defined in routes/routes.js)
     * @param {object} - options - forwarded to Next.js ()
     */
    const handleRoute =
      (method = 'pushRoute', callback = () => {}) =>
      async (route, params = {}) => {
        const routePath = getRoutePath({ routeName: route, params, locale: router.asPath.split('/')[1] });

        const resolvedMethod = resolveMethod(method);

        let response;

        if (resolvedMethod === 'pushRoute') {
          response = await router.push(routePath);
        } else if (resolvedMethod === 'replaceRoute') {
          response = await router.replace(routePath);
        } else if (resolvedMethod === 'prefetchRoute') {
          response = await router.prefetch(routePath);
        }

        callback();
        return response;
      };

    const scrollToTopAndFocus = () => {
      window.scrollTo(0, 0);
      document.body.focus();
    };

    const buildProps = () => {
      const { router: propsRouter } = props;
      const blacklistRouterProps = ['components', 'push', 'prefetch', 'replace'];
      const passThroughRouterProps = omit(propsRouter, blacklistRouterProps);

      return {
        ...passThroughRouterProps,
        pushRoute: handleRoute('pushRoute'),
        pushRouteAndScroll: handleRoute('pushRoute', scrollToTopAndFocus),
        replaceRoute: handleRoute('replaceRoute'),
        replaceRouteAndScroll: handleRoute('replaceRoute', scrollToTopAndFocus),
        prefetchRoute: handleRoute('prefetchRoute')
      };
    };

    // blacklist activeLocale as we don't need it doubled up anywhere
    const blacklistProps = ['router', 'activeLocale'];
    const passThroughProps = omit(props, blacklistProps);

    return <BaseComponent routerReplace={router.replace} router={buildProps()} {...passThroughProps} />;
  }

  ComponentWithRouter.propTypes = {
    router: PropTypes.shape({}),
    ...withActiveLocalePropType
  };
  ComponentWithRouter.defaultProps = {
    router: {}
  };

  return compose(withRouter, withActiveLocale)(ComponentWithRouter);
}

export default withRouting;
